// Shared access to the document's Paper & Surface for the Canvas UI (File menu
// flyout, Paper tab). The document surface lives in the physical paint system;
// the UI only talks to it through window events:
//   vx-paint-surface-query  ask the paint system to announce its surface
//   vx-paint-surface        the paint system's current surface (detail)
//   vx-paint-surface-set    change the surface (detail is merged into it)
//   vx-open-paper-settings  open the Paper tab of the Canvas dock ({ advanced })
// "available" means a physical paint system answered, so the surface really
// belongs to this document (without WebGPU or the flag there is none).

import { useEffect, useState } from 'react';
import { DEFAULT_SURFACE_SETTINGS } from '../surfaces/SurfaceLibrary.js';
import { subscribePaintDevice } from './physicalPaintFlag.js';

export function queryPaperSurface() {
  if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('vx-paint-surface-query'));
}

export function setPaperSurface(patch) {
  if (typeof window === 'undefined' || !patch || typeof patch !== 'object') return;
  window.dispatchEvent(new CustomEvent('vx-paint-surface-set', { detail: { ...patch } }));
}

export function openPaperSettings({ advanced = false } = {}) {
  if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('vx-open-paper-settings', { detail: { advanced: Boolean(advanced) } }));
}

export function usePaperSurface() {
  const [state, setState] = useState({ surface: { ...DEFAULT_SURFACE_SETTINGS }, available: false });

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onSurface = (event) => {
      if (!event || !event.detail) return;
      setState((current) => ({ surface: { ...current.surface, ...event.detail }, available: true }));
    };
    window.addEventListener('vx-paint-surface', onSurface);
    queryPaperSurface();
    // The paint system may be created after this component (WebGPU device
    // acquisition is asynchronous): ask again once the device is ready.
    const unsubscribe = subscribePaintDevice((device) => {
      if (device) window.setTimeout(queryPaperSurface, 0);
      else setState((current) => ({ ...current, available: false }));
    });
    return () => {
      window.removeEventListener('vx-paint-surface', onSurface);
      unsubscribe();
    };
  }, []);

  return state;
}
